import React from 'react';
import { Tabs } from 'antd';
import { Info, MessageCircle } from 'lucide-react';
import { IconPill } from '@doer/frontend-components/icon-pill';
import {
  CreativityContentData,
  isFormCreativity,
  isPromptCreativity,
  PromptCreativityContent
} from '../../../types/banner';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

import { ConfigurationCard } from './cards/configuration-card';
import { GeneralInfoCard } from './cards/general-info-card';
import { FeedbackCard } from './cards/feedback-card';
import { ChatPanel } from '../chat-panel/chat-panel';

interface AssetInfoPanelProps {
  creativity: CreativityContentData;
  clientId: string;
  onDeleted?: () => void;
  onClose: () => void;
}

export const AssetInfoPanel: React.FC<AssetInfoPanelProps> = ({ creativity, clientId, onDeleted, onClose }) => {
  const { spacing, screens } = useResponsiveSizes();
  const showChat = isPromptCreativity(creativity);

  const defaultTab = showChat ? 'chat' : 'info';
  const [activeTab, setActiveTab] = React.useState<string>(defaultTab);

  const tabContentHeight = screens.xs
    ? '50vh' // móviles pequeños
    : screens.sm
      ? '55vh' // tablets
      : screens.md
        ? '58vh' // laptops pequeñas
        : '60vh'; // desktop

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: spacing.lg
      }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size='small'
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        items={[
          {
            key: 'info',
            label: (
              <IconPill selected={activeTab === 'info'}>
                <Info size={20} />
              </IconPill>
            ),
            children: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.lg,
                  height: tabContentHeight,
                  overflowY: 'auto',
                  paddingRight: spacing.sm,
                  marginTop: spacing.xs,
                  scrollbarColor: '#e6e6e6 #FFFFFF'
                }}
              >
                <GeneralInfoCard creativity={creativity} onDeleted={onDeleted} onClose={onClose} />

                <FeedbackCard creativity={creativity} />

                {isFormCreativity(creativity) && creativity.bannerType && (
                  <ConfigurationCard creativity={creativity} clientId={clientId} />
                )}
              </div>
            )
          },
          {
            key: 'chat',
            label: (
              <IconPill selected={activeTab === 'chat'}>
                <MessageCircle size={20} />
              </IconPill>
            ),
            disabled: !showChat,
            children: showChat ? (
              <div style={{ height: tabContentHeight }}>
                <ChatPanel creativity={creativity as PromptCreativityContent} />
              </div>
            ) : null
          }
        ]}
      />
    </div>
  );
};
